import type { RefObject } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { sendFeedbackInputSchema, type SendFeedbackInput } from "@repo/shared";
import { useTranslation } from "react-i18next";
import {
  Button,
  DialogClose,
  Field,
  Form,
  FormActions,
  FormDrawer,
  Input,
  Textarea,
} from "@h2j-data-team/ui";

import { toast } from "@/components/shared";
import { sendFeedback } from "@/lib/api/feedback";
import { getErrorMessage } from "@/lib/api-client";
import { discardMessages } from "@/lib/ui-labels";

interface FeedbackDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail: string;
  userName?: string | null;
  /** What opened the drawer (it has no Trigger): focus goes back to it on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

export function FeedbackDrawer({
  open,
  onOpenChange,
  userEmail,
  userName,
  restoreFocusRef,
}: FeedbackDrawerProps) {
  const { t } = useTranslation("common");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<SendFeedbackInput>({
    resolver: zodResolver(sendFeedbackInputSchema),
    defaultValues: { subject: "", message: "" },
  });

  const onSubmit = async (data: SendFeedbackInput) => {
    try {
      await sendFeedback(data);
      toast.success(t("sendFeedback.successToast"));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, t("sendFeedback.errorToast")));
    }
  };

  // Closing (after the guard accepted the discard) starts the next feedback blank.
  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <FormDrawer
      open={open}
      onOpenChange={handleOpenChange}
      title={t("sendFeedback.title")}
      description={t("sendFeedback.description")}
      closeLabel={t("close")}
      size="sm"
      busy={isSubmitting}
      dirty={isDirty}
      discardConfirmation={discardMessages()}
      restoreFocusRef={restoreFocusRef}
      footer={
        <FormActions
          busy={isSubmitting}
          submitLabel={t("sendFeedback.submit")}
          submittingLabel={t("sendFeedback.sending")}
          submitProps={{ form: "feedback-form" }}
        >
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("cancel")}
            </Button>
          </DialogClose>
        </FormActions>
      }
    >
      <Form id="feedback-form" busy={isSubmitting} onSubmit={handleSubmit(onSubmit)}>
        <Field label={t("sendFeedback.fromLabel")}>
          <Input value={userName ? `${userName} (${userEmail})` : userEmail} disabled />
        </Field>
        <Field label={t("sendFeedback.subjectLabel")} required error={errors.subject?.message}>
          <Input placeholder={t("sendFeedback.subjectPlaceholder")} {...register("subject")} />
        </Field>
        <Field label={t("sendFeedback.messageLabel")} required error={errors.message?.message}>
          <Textarea
            placeholder={t("sendFeedback.messagePlaceholder")}
            rows={5}
            {...register("message")}
          />
        </Field>
      </Form>
    </FormDrawer>
  );
}
